/**
 * Acha a MENOR compressão que faz uma imagem caber no teto do canal.
 *
 * "Menor" em ordem de estrago visível:
 *
 *   1. PNG em resolução cheia (só quando a origem não tinha perda) — zero perda;
 *   2. JPEG em resolução cheia, com a MAIOR qualidade que couber;
 *   3. JPEG reduzido, com a MAIOR escala que couber.
 *
 * Imagem com transparência fica em PNG e só perde resolução: achatar o alfa
 * num fundo branco muda o conteúdo (logo, figurinha, print recortado), o que é
 * pior que uma imagem um pouco menor.
 *
 * O tamanho que um codificador produz não tem fórmula fechada — depende do
 * conteúdo. Então a busca é empírica: codifica, mede, e bissecta. Tamanho
 * cresce com qualidade e com escala, então a bissecção converge. Pura: o
 * codificador é injetado, o que deixa a busca testável sem canvas.
 */

export type FormatoDeSaida = 'image/jpeg' | 'image/png';

export interface Tentativa {
    formato: FormatoDeSaida;
    /** Fração das dimensões originais, (0, 1]. */
    escala: number;
    /** Só JPEG: 0..1. */
    qualidade?: number;
}

export type Codificar = (tentativa: Tentativa) => Promise<Blob>;

export interface EntradaDaBusca {
    limiteBytes: number;
    temTransparencia: boolean;
    /** Origem sem perda E fora dos formatos aceitos: vale tentar PNG cheio antes. */
    tentarPngCheio: boolean;
    codificar: Codificar;
    signal?: AbortSignal;
    onProgress?: (fracao: number) => void;
}

export type ResultadoDaBusca =
    | { estado: 'ok'; blob: Blob; tentativa: Tentativa }
    | { estado: 'impossivel' };

export const QUALIDADE_MAXIMA = 0.92;
export const QUALIDADE_MINIMA = 0.6;
/** Qualidade usada quando a resolução é que cede — reduzir E esmagar dobra o estrago. */
export const QUALIDADE_AO_REDUZIR = 0.85;
export const ESCALA_MINIMA = 0.05;
/** Cada passo divide o intervalo por dois: 6 passos ≈ 1,5% de precisão. */
export const PASSOS_DE_BISSECCAO = 6;

/** Pior caso de codificações, para o progresso ter denominador fixo. */
function totalDeCodificacoes(entrada: EntradaDaBusca): number {
    const buscaDeEscala = 2 + PASSOS_DE_BISSECCAO; // palpite + piso + bissecção
    if (entrada.temTransparencia) return 1 + buscaDeEscala;
    return (entrada.tentarPngCheio ? 1 : 0) + 2 + PASSOS_DE_BISSECCAO + buscaDeEscala;
}

export async function buscarMenorCompressao(entrada: EntradaDaBusca): Promise<ResultadoDaBusca> {
    const { limiteBytes, signal } = entrada;
    const total = totalDeCodificacoes(entrada);
    let feitas = 0;

    const medir = async (tentativa: Tentativa) => {
        if (signal?.aborted) throw new DOMException('Conversão cancelada.', 'AbortError');
        const blob = await entrada.codificar(tentativa);
        feitas++;
        entrada.onProgress?.(Math.min(1, feitas / total));
        return { blob, tentativa, cabe: blob.size <= limiteBytes };
    };

    const concluir = (blob: Blob, tentativa: Tentativa): ResultadoDaBusca => {
        entrada.onProgress?.(1);
        return { estado: 'ok', blob, tentativa };
    };

    /**
     * Maior escala que cabe, dado o tamanho em escala 1 (que NÃO coube).
     *
     * Bytes crescem ~ com a área, então `√(limite/tamanho)` é um bom primeiro
     * palpite; a bissecção corrige o resto.
     */
    const buscarEscala = async (formato: FormatoDeSaida, qualidade: number | undefined, tamanhoCheio: number) => {
        const palpite = Math.min(0.99, Math.max(ESCALA_MINIMA, Math.sqrt(limiteBytes / tamanhoCheio) * 0.95));
        let cabe: { blob: Blob; tentativa: Tentativa } | null = null;
        let baixo = ESCALA_MINIMA;
        let alto = 1;

        const p = await medir({ formato, escala: palpite, qualidade });
        if (p.cabe) { cabe = p; baixo = palpite; }
        else {
            alto = palpite;
            const piso = await medir({ formato, escala: ESCALA_MINIMA, qualidade });
            if (!piso.cabe) return null;
            cabe = piso;
        }

        for (let i = 0; i < PASSOS_DE_BISSECCAO; i++) {
            const meio = (baixo + alto) / 2;
            const m = await medir({ formato, escala: meio, qualidade });
            if (m.cabe) { cabe = m; baixo = meio; } else { alto = meio; }
        }
        return cabe;
    };

    if (entrada.temTransparencia) {
        const cheio = await medir({ formato: 'image/png', escala: 1 });
        if (cheio.cabe) return concluir(cheio.blob, cheio.tentativa);
        const reduzido = await buscarEscala('image/png', undefined, cheio.blob.size);
        return reduzido ? concluir(reduzido.blob, reduzido.tentativa) : { estado: 'impossivel' };
    }

    if (entrada.tentarPngCheio) {
        const png = await medir({ formato: 'image/png', escala: 1 });
        if (png.cabe) return concluir(png.blob, png.tentativa);
    }

    const melhor = await medir({ formato: 'image/jpeg', escala: 1, qualidade: QUALIDADE_MAXIMA });
    if (melhor.cabe) return concluir(melhor.blob, melhor.tentativa);

    const pior = await medir({ formato: 'image/jpeg', escala: 1, qualidade: QUALIDADE_MINIMA });
    if (pior.cabe) {
        // Bissecção na qualidade: a maior que ainda cabe, em resolução cheia.
        let cabe = pior;
        let baixo = QUALIDADE_MINIMA;
        let alto = QUALIDADE_MAXIMA;
        for (let i = 0; i < PASSOS_DE_BISSECCAO; i++) {
            const meio = (baixo + alto) / 2;
            const m = await medir({ formato: 'image/jpeg', escala: 1, qualidade: meio });
            if (m.cabe) { cabe = m; baixo = meio; } else { alto = meio; }
        }
        return concluir(cabe.blob, cabe.tentativa);
    }

    // Nem a qualidade mínima cabe em resolução cheia: a resolução cede.
    // O palpite usa o tamanho na qualidade MÁXIMA — superestima o da qualidade
    // de redução, então erra para uma escala menor, que a bissecção sobe.
    const reduzido = await buscarEscala('image/jpeg', QUALIDADE_AO_REDUZIR, melhor.blob.size);
    return reduzido ? concluir(reduzido.blob, reduzido.tentativa) : { estado: 'impossivel' };
}
